class GoodElevationStyleQuiz extends HTMLElement {
  connectedCallback() {
    this.form = this.querySelector('[data-ge-quiz-form]');
    this.steps = [...this.querySelectorAll('[data-ge-step]')];
    this.previousButton = this.querySelector('[data-ge-previous]');
    this.nextButton = this.querySelector('[data-ge-next]');
    this.submitButton = this.querySelector('[data-ge-submit]');
    this.saveQuizButton = this.querySelector('[data-ge-save-quiz]');
    this.accountEntry = this.querySelector('[data-ge-account-entry]');
    // v56: remove any stale/legacy body-shape cards if older theme markup survives a Shopify update.
    const allowedShapes = {
      Women: new Set(['Triangle','Oval','Inverted Triangle','Rectangle','Hourglass','Diamond']),
      Men: new Set(['Triangle','Oval','Inverted Triangle','Rectangle','Trapezoid','Diamond'])
    };
    this.querySelectorAll('[data-ge-body-group]').forEach((group) => {
      const gender = group.dataset.geBodyGroup;
      group.querySelectorAll('.ge-quiz__shape-choice').forEach((card) => {
        const input = card.querySelector('input[name="body_shape"]');
        if (!input || !allowedShapes[gender] || !allowedShapes[gender].has(input.value)) card.remove();
      });
    });
    this.nameInput = this.querySelector('[name="customerName"]');
    this.emailInput = this.querySelector('[name="customerEmail"]');
    this.consentInput = this.querySelector('[name="privacyConsent"]');
    this.error = this.querySelector('[data-ge-quiz-error]');
    this.progressText = this.querySelector('[data-ge-progress-text]');
    this.progressCategory = this.querySelector('[data-ge-progress-category]');
    this.progressBar = this.querySelector('[data-ge-progress-bar]');
    this.currentStep = 0;
    this.resultsUrl = this.dataset.resultsUrl || '/pages/your-style-results-1';
    this.updateMode = new URLSearchParams(window.location.search).get('update') === '1';
    this.customerLoggedIn = this.dataset.customerLoggedIn === 'true';
    this.trackedSteps = new Set();
    window.GoodElevationAnalytics?.track('quiz_started', { update_mode: this.updateMode });

    // Returning anonymous visitors should always be able to start the quiz again.
    // Only signed-in customers with an existing Style DNA may skip straight to results.
    if (this.customerLoggedIn && !this.updateMode && !window.Shopify?.designMode && window.localStorage.getItem('goodElevationStyleDNA')) {
      window.location.replace(this.resultsUrl);
      return;
    }
    const resumeSavedQuiz = new URLSearchParams(window.location.search).get('resume') === '1' && window.localStorage.getItem('goodElevationQuizDraft');

    this.previousButton.addEventListener('click', () => this.changeStep(-1));
    this.nextButton.addEventListener('click', () => {
      if (this.validateCurrentStep()) this.changeStep(1);
    });
    this.form.addEventListener('submit', (event) => this.submit(event));
    this.saveQuizButton?.addEventListener('click', () => this.saveQuiz());
    [this.nameInput, this.emailInput, this.consentInput].filter(Boolean).forEach((input) => {
      input.addEventListener('input', () => this.updateFinalActions());
      input.addEventListener('change', () => this.updateFinalActions());
    });
    this.querySelectorAll('[name="gender"]').forEach((input) => input.addEventListener('change', () => this.updateBodyTypes()));
    this.querySelectorAll('[name="occasion"]').forEach((input) => input.addEventListener('change', () => this.updateWeekendFields()));
    this.querySelectorAll('[data-ge-colour]').forEach((input) => input.addEventListener('change', () => this.limitChecks('[data-ge-colour]', input, 3, 'colours')));
    this.querySelectorAll('[data-ge-fabric]').forEach((input) => input.addEventListener('change', () => this.limitChecks('[data-ge-fabric]', input, 3, 'fabric qualities')));
    this.querySelectorAll('[data-ge-brand]').forEach((input) => input.addEventListener('change', () => this.updateBrandSelection(input)));
    this.querySelectorAll('[data-ge-none-for]').forEach((input) => input.addEventListener('change', () => this.applyNoneChoice(input)));
    this.restore();
    this.applyUrlPreset();
    if (resumeSavedQuiz) this.currentStep = this.steps.length - 1;
    this.querySelectorAll('[data-ge-none-for]:checked').forEach((input) => this.applyNoneChoice(input));
    this.updateBrandSelection();
    this.render();
  }


  applyUrlPreset() {
    const params = new URLSearchParams(window.location.search);
    const genderParam = params.get('gender');
    const occasionParam = params.get('occasion') || params.get('style');
    const gender = /^(women|woman|female)$/i.test(genderParam || '') ? 'Women'
      : /^(men|man|male)$/i.test(genderParam || '') ? 'Men' : '';
    if (gender) {
      const input = [...this.querySelectorAll('[name="gender"]')].find((el) => el.value === gender);
      if (input) input.checked = true;
      this.updateBodyTypes();
    }
    if (occasionParam) {
      const normalized = String(occasionParam).toLowerCase().replace(/[-_]+/g, ' ').trim();
      const target = [...this.querySelectorAll('[name="occasion"]')].find((el) => {
        const v = String(el.value || '').toLowerCase().trim();
        return v === normalized || (normalized === 'weekend' && v === 'weekend wear');
      });
      if (target && !target.disabled) target.checked = true;
      this.updateWeekendFields();
    }
    if (gender || occasionParam) this.saveDraft();
  }

  validateCurrentStep() {
    const step = this.steps[this.currentStep];
    const controls = [...step.querySelectorAll('[required]')].filter((control) => !control.disabled);
    let firstInvalid = null;
    const validRequired = controls.every((control) => {
      const valid = control.checkValidity() && String(control.value || '').trim();
      if (!valid && !firstInvalid) firstInvalid = control;
      return valid;
    });
    const radioNames = new Set(controls.filter((control) => control.type === 'radio').map((control) => control.name));
    const validRadios = [...radioNames].every((name) => {
      const valid = [...step.querySelectorAll(`[name="${name}"]`)].filter((control) => !control.disabled).some((control) => control.checked);
      if (!valid && !firstInvalid) firstInvalid = step.querySelector(`[name="${name}"]`);
      return valid;
    });
    const category = step.dataset.category;
    const validColours = category !== 'Colours you enjoy' || this.querySelectorAll('[data-ge-colour]:checked').length > 0;
    const validBody = category !== 'Body type' || this.querySelectorAll('[data-ge-body]:checked').length === 1;
    const validFabrics = category !== 'Patterns & fabrics' || this.querySelectorAll('[data-ge-fabric]:checked').length > 0;
    const valid = validRequired && validRadios && validColours && validBody && validFabrics;

    this.error.hidden = valid;
    if (!valid) {
      this.error.textContent = firstInvalid?.validationMessage || 'Please complete this section before continuing.';
      firstInvalid?.focus({ preventScroll: true });
    }
    return valid;
  }

  updateBodyTypes() {
    const gender = this.form.elements.gender?.value || '';
    if (gender) this.dataset.geGender = gender;
    this.querySelectorAll('[data-ge-fit-image]').forEach((image) => {
      const source = gender === 'Men' ? image.dataset.geMenSrc : image.dataset.geWomenSrc;
      if (source && image.src !== source) image.src = source;
    });
    this.querySelectorAll('[data-ge-occasion-gender]').forEach((choice) => {
      const active = choice.dataset.geOccasionGender === gender;
      choice.querySelectorAll('input').forEach((input) => {
        input.disabled = !active;
        if (!active) input.checked = false;
      });
    });
    this.querySelectorAll('[data-ge-body-group]').forEach((group) => {
      const active = group.dataset.geBodyGroup === gender;
      group.hidden = !active;
      group.querySelectorAll('input').forEach((input) => {
        input.disabled = !active;
        input.required = active;
        if (!active) input.checked = false;
      });
    });
  }

  updateWeekendFields() {
    const weekend = this.form.elements.occasion?.value === 'Weekend Wear';
    const field = this.querySelector('[data-ge-weekend-field]');
    if (!field) return;
    field.hidden = !weekend;
    field.querySelectorAll('input').forEach((input) => {
      input.disabled = !weekend;
      input.required = weekend;
      if (!weekend) input.checked = false;
    });
  }

  applyNoneChoice(input) {
    const names = String(input.dataset.geNoneFor || '').split(',').filter(Boolean);
    names.forEach((name) => {
      this.querySelectorAll(`[name="${name}"]`).forEach((control) => {
        if (input.checked) control.checked = false;
        control.disabled = input.checked;
      });
    });
  }

  limitChecks(selector, changedInput, maximum, label) {
    const selected = [...this.querySelectorAll(`${selector}:checked`)];
    if (selected.length > maximum) {
      changedInput.checked = false;
      this.error.textContent = `Please select no more than ${maximum} ${label}.`;
      this.error.hidden = false;
    } else {
      this.error.hidden = true;
    }
  }

  updateBrandSelection(changedInput = null) {
    const inputs = [...this.querySelectorAll('[data-ge-brand]')];
    let selected = inputs.filter((input) => input.checked);
    if (selected.length > 3 && changedInput) {
      changedInput.checked = false;
      selected = inputs.filter((input) => input.checked);
      this.error.textContent = 'Please choose a maximum of 3 brands.';
      this.error.hidden = false;
    } else if (this.error.textContent === 'Please choose a maximum of 3 brands.') {
      this.error.hidden = true;
    }

    const atLimit = selected.length >= 3;
    inputs.forEach((input) => {
      input.disabled = atLimit && !input.checked;
      input.closest('.ge-quiz__brand-choice')?.classList.toggle('is-disabled', input.disabled);
    });

    const counter = this.querySelector('[data-ge-brand-count]');
    if (counter) counter.textContent = `${selected.length} of 3 brands selected`;
  }

  changeStep(direction) {
    this.saveDraft();
    this.currentStep = Math.max(0, Math.min(this.steps.length - 1, this.currentStep + direction));
    this.error.hidden = true;
    this.render();
    this.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  render() {
    this.steps.forEach((step, index) => {
      const active = index === this.currentStep;
      step.hidden = !active;
      step.classList.toggle('is-active', active);
    });
    this.previousButton.hidden = this.currentStep === 0;
    this.nextButton.hidden = this.currentStep === this.steps.length - 1;
    this.submitButton.hidden = this.currentStep !== this.steps.length - 1;
    this.progressText.textContent = `${this.currentStep + 1} of ${this.steps.length}`;
    this.progressCategory.textContent = this.steps[this.currentStep].dataset.category;
    this.progressBar.style.width = `${((this.currentStep + 1) / this.steps.length) * 100}%`;
    if (this.steps[this.currentStep].dataset.category === 'Body type') this.updateBodyTypes();
    if (this.steps[this.currentStep].dataset.category === 'Garment fit') this.updateWeekendFields();
    if (this.currentStep === this.steps.length - 1) this.renderReview();
    if (this.accountEntry) this.accountEntry.hidden = this.currentStep !== 0;
    this.updateFinalActions();
    if (!this.trackedSteps.has(this.currentStep)) {
      this.trackedSteps.add(this.currentStep);
      window.GoodElevationAnalytics?.track('quiz_step_viewed', {
        step: this.currentStep + 1,
        total_steps: this.steps.length,
        category: this.steps[this.currentStep].dataset.category || ''
      });
    }
  }

  finalDetailsValid() {
    const nameValid = Boolean(this.nameInput?.value.trim());
    const emailValid = Boolean(this.emailInput?.value.trim()) && this.emailInput.checkValidity();
    const consentValid = Boolean(this.consentInput?.checked);
    return nameValid && emailValid && consentValid;
  }

  updateFinalActions() {
    const isFinal = this.currentStep === this.steps.length - 1;
    if (this.saveQuizButton) {
      this.saveQuizButton.hidden = !isFinal;
      const disabled = !isFinal || !this.finalDetailsValid();
      this.saveQuizButton.disabled = disabled;
      this.saveQuizButton.setAttribute('aria-disabled', String(disabled));
    }
    if (this.submitButton) {
      const disabled = isFinal && !this.finalDetailsValid();
      this.submitButton.disabled = disabled;
      this.submitButton.setAttribute('aria-disabled', String(disabled));
    }
  }

  saveQuiz() {
    if (!this.finalDetailsValid()) {
      this.validateCurrentStep();
      return;
    }
    const profile = this.profileFromForm();
    window.localStorage.setItem('goodElevationCustomerProfile', JSON.stringify({
      name: profile.customerName,
      email: profile.customerEmail
    }));
    window.localStorage.setItem('goodElevationQuizDraft', JSON.stringify(profile));
    window.localStorage.setItem('goodElevationQuizSavedAt', String(Date.now()));
    window.GoodElevationAnalytics?.track('quiz_saved', {
      gender: profile.gender, occasion: profile.occasion, budget: profile.budget
    });
    if (this.customerLoggedIn) {
      this.saveQuizButton.innerHTML = '<span aria-hidden="true">♥</span> Quiz saved';
      this.saveQuizButton.classList.add('is-saved');
      return;
    }
    const returnUrl = `${window.location.pathname}?resume=1`;
    window.location.assign(`/account/login?return_url=${encodeURIComponent(returnUrl)}`);
  }

  getAll(data, name) {
    return data.getAll(name).filter(Boolean);
  }

  profileFromForm() {
    const data = new FormData(this.form);
    const waist = data.get('waist') || '';
    return {
      customerName: data.get('customerName') || '',
      customerEmail: data.get('customerEmail') || '',
      gender: data.get('gender') || '',
      occasion: data.get('occasion') || '',
      style: data.get('occasion') || '',
      stylePath: data.get('occasion') || '',
      budget: Number(data.get('budget') || 0),
      body_shape: data.get('body_shape') || '',
      age: data.get('age') || '',
      waist,
      waistInches: waist,
      inseam: data.get('inseam') || '',
      blazerSize: data.get('blazerSize') || '',
      height: data.get('height') || '',
      weight: data.get('weight') || '',
      topSize: data.get('topSize') || '',
      bottomSize: data.get('bottomSize') || '',
      size: data.get('topSize') || '',
      shoeSize: data.get('shoeSize') || '',
      shoe_size: data.get('shoeSize') || '',
      usualSizeFit: data.get('usualSizeFit') || '',
      pantFit: data.get('pantFit') || '',
      shirtFit: data.get('shirtFit') || '',
      shortsFit: data.get('shortsFit') || '',
      fitPreference: `${data.get('shirtFit') || ''} shirt; ${data.get('pantFit') || ''} pants`,
      torsoProportion: data.get('torsoProportion') || '',
      legProportion: data.get('legProportion') || '',
      risePreference: data.get('risePreference') || '',
      colours: this.getAll(data, 'colours').slice(0, 3),
      avoidColours: data.get('avoidColoursNone') ? [] : this.getAll(data, 'avoidColours'),
      avoidPatterns: data.get('avoidPatternsNone') ? [] : this.getAll(data, 'avoidPatterns'),
      fabrics: this.getAll(data, 'fabrics').slice(0, 3),
      preferredBrands: this.getAll(data, 'preferredBrands').slice(0, 3),
      preferredJeansBrands: data.get('preferredJeansBrandsNone') ? [] : this.getAll(data, 'preferredJeansBrands'),
      preferredSneakerBrands: data.get('preferredSneakerBrandsNone') ? [] : this.getAll(data, 'preferredSneakerBrands'),
      preferredJeansBrandsNone: data.get('preferredJeansBrandsNone') || '',
      preferredSneakerBrandsNone: data.get('preferredSneakerBrandsNone') || '',
      avoidItems: data.get('avoidItemsNone') ? [] : this.getAll(data, 'avoidItems'),
      avoidShoes: data.get('avoidItemsNone') ? [] : this.getAll(data, 'avoidShoes'),
      recommendationCount: 5,
      itemsPerLook: 3,
      completeLooksOnly: true,
      strictGenderMatch: true,
      quiz_id: 'good-elevation-style-dna-v5'
    };
  }

  aiProfile(profile) {
    const { customerName, customerEmail, ...stylingProfile } = profile;
    return stylingProfile;
  }

  saveDraft() {
    window.localStorage.setItem('goodElevationQuizDraft', JSON.stringify(this.profileFromForm()));
  }

  restore() {
    let draft = {};
    try {
      const source = window.localStorage.getItem('goodElevationQuizDraft')
        || (this.updateMode ? window.localStorage.getItem('goodElevationStyleDNA') : '')
        || window.sessionStorage.getItem('goodElevationQuizDraft')
        || '{}';
      draft = JSON.parse(source);
      if (this.updateMode) {
        const customer = JSON.parse(window.localStorage.getItem('goodElevationCustomerProfile') || '{}');
        draft.customerName = customer.name || draft.customerName || '';
        draft.customerEmail = customer.email || draft.customerEmail || '';
      }
    } catch (_) { draft = {}; }
    Object.entries(draft).forEach(([name, value]) => {
      const controls = [...this.form.querySelectorAll(`[name="${name}"]`)];
      controls.forEach((control) => {
        if (control.type === 'radio' || control.type === 'checkbox') {
          control.checked = Array.isArray(value) ? value.includes(control.value) : control.value === String(value);
        } else if (value != null) {
          control.value = value;
        }
      });
    });
    this.updateBodyTypes();
    this.updateWeekendFields();
  }

  renderReview() {
    const profile = this.profileFromForm();
    const review = this.querySelector('[data-ge-review]');
    review.innerHTML = `
      <p><strong>Ready, ${this.escape(profile.customerName || 'there')}.</strong></p>
      <p>${this.escape(profile.gender)} · ${this.escape(profile.occasion)} · Up to R${Number(profile.budget).toLocaleString('en-ZA')}</p>
      <p>${this.escape(profile.colours.join(', '))} · Top ${this.escape(profile.topSize)} · Waist ${this.escape(profile.waistInches)} · Shoe ${this.escape(profile.shoeSize)}</p>
      <p>Jeans brands: ${this.escape(profile.preferredJeansBrands.join(', ') || 'No preference')}</p>
      <p>Sneaker brands: ${this.escape(profile.preferredSneakerBrands.join(', ') || 'No preference')}</p>
      ${profile.preferredBrands.length ? `<p>Preferred brands: ${this.escape(profile.preferredBrands.join(', '))}</p>` : ''}
    `;
  }

  submit(event) {
    event.preventDefault();
    if (!this.validateCurrentStep()) return;
    const profile = this.profileFromForm();
    window.localStorage.setItem('goodElevationCustomerProfile', JSON.stringify({
      name: profile.customerName,
      email: profile.customerEmail
    }));
    window.localStorage.setItem('goodElevationStyleDNA', JSON.stringify(this.aiProfile(profile)));
    window.GoodElevationAnalytics?.track('quiz_completed', {
      gender: profile.gender,
      occasion: profile.occasion,
      budget: profile.budget,
      body_shape: profile.body_shape,
      colours: profile.colours,
      preferred_brands: profile.preferredBrands
    });
    window.localStorage.removeItem('goodElevationQuizDraft');
    window.sessionStorage.removeItem('goodElevationQuizDraft');
    window.location.assign(this.resultsUrl);
  }

  escape(value) {
    const node = document.createElement('div');
    node.textContent = String(value ?? '');
    return node.innerHTML;
  }
}

if (!customElements.get('good-elevation-style-quiz')) {
  customElements.define('good-elevation-style-quiz', GoodElevationStyleQuiz);
}
